import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import LegalLayout from './LegalLayout'

const DEFAULT_TERMS_BODY =
  'These Terms of Service govern the provision of private passenger transfers, airport connections, ski resort transportation, and customized day trips provided by Salzburg Cab. By reserving a transfer or using our website, you agree to be bound by these terms.'

export default function TermsOfService() {
  const [adminCustomText, setAdminCustomText] = useState<string | null>(null)

  useEffect(() => {
    try {
      const stored = localStorage.getItem('salzburg_dashboard_terms_conditions')
      if (stored && stored.trim().length > 0) {
        setAdminCustomText(stored.trim())
      }
    } catch {
      // fallback
    }
  }, [])

  return (
    <LegalLayout
      activeTab="terms"
      title="Terms of Service"
      subtitle="The contractual terms and service conditions governing bookings and passenger transport with Salzburg Cab."
      lastUpdated="January 2026"
    >
      <div className="space-y-8 font-host text-gray-700 leading-relaxed text-sm sm:text-[15px]">
        {/* Intro */}
        <section className="border-b border-gray-100 pb-6">
          <p className="text-gray-900 font-medium text-base sm:text-lg">
            Standard Transport Terms & Booking Agreement
          </p>
          <p className="mt-3 text-gray-600">
            {adminCustomText || DEFAULT_TERMS_BODY}
          </p>
        </section>

        {/* Section 1: Scope & Contract Formation */}
        <section className="space-y-3">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">
            1. Scope of Service & Contract Formation
          </h2>
          <p className="text-gray-600">
            Salzburg Cab provides licensed passenger transport services, including airport transfers, intercity transfers, ski resort shuttles, and private chauffeur tours throughout Austria and neighboring European countries.
          </p>
          <p className="text-gray-600">
            A binding transport contract is formed upon receipt of a booking confirmation email or SMS voucher issued by Salzburg Cab following your reservation request.
          </p>
        </section>

        {/* Section 2: Pricing, Tolls & Payments */}
        <section className="space-y-3">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">
            2. Rates, Tolls & Payment Terms
          </h2>
          <ul className="list-disc pl-5 space-y-2 text-gray-600">
            <li>
              <strong className="text-gray-900">Transparent Pricing:</strong> Confirmed transfer rates are guaranteed and include Austrian statutory VAT, Austrian highway vignette fees, and vehicle insurance.
            </li>
            <li>
              <strong className="text-gray-900">Tolls & Parking:</strong> Standard routes include toll fees. Any optional panoramic scenic detour routes or non-standard parking fees requested by the client during the trip will be billed at cost.
            </li>
            <li>
              <strong className="text-gray-900">Accepted Payment Methods:</strong> We accept major credit/debit cards (Visa, MasterCard, American Express), digital payments, and cash in Euro (€) paid directly to the driver upon completion of the trip.
            </li>
          </ul>
        </section>

        {/* Section 3: Airport Pickups & Flight Delays */}
        <section className="space-y-3">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">
            3. Airport Pickups & Flight Monitoring
          </h2>
          <p className="text-gray-600">
            For airport arrivals (Salzburg Airport SZG, Munich Airport MUC, Vienna Airport VIE, Innsbruck INN, etc.):
          </p>
          <div className="rounded-xl bg-gray-50 border border-gray-200/70 p-4 space-y-2 text-gray-700 text-xs sm:text-sm">
            <p>
              &bull; <strong>Flight Tracking:</strong> We monitor commercial flight arrival statuses in real time using the flight number provided during booking.
            </p>
            <p>
              &bull; <strong>Complimentary Waiting Time:</strong> 60 minutes of complimentary waiting time is included from the actual aircraft touchdown time to allow for border control and baggage claim.
            </p>
            <p>
              &bull; <strong>Meeting Point:</strong> Your assigned chauffeur will wait in the arrivals hall holding an personalized digital meeting board or physical name sign.
            </p>
          </div>
        </section>

        {/* Section 4: Passenger Conduct & Vehicle Care */}
        <section className="space-y-3">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">
            4. Passenger Obligations & Safety
          </h2>
          <ul className="list-disc pl-5 space-y-2 text-gray-600">
            <li>
              <strong className="text-gray-900">Non-Smoking Policy:</strong> All Salzburg Cab vehicles maintain a strict 100% smoke-free environment (including electronic cigarettes and vapes).
            </li>
            <li>
              <strong className="text-gray-900">Seat Belts & Child Seats:</strong> In compliance with Austrian road safety laws, all passengers must wear seat belts while the vehicle is in motion. Child safety and booster seats must be reserved in advance.
            </li>
            <li>
              <strong className="text-gray-900">Luggage Limits:</strong> Passengers must adhere to the luggage allowances specified for their selected vehicle category. Excessive baggage that cannot safely fit in the vehicle trunk cannot be accommodated.
            </li>
            <li>
              <strong className="text-gray-900">Vehicle Cleanliness:</strong> A cleaning fee will apply if a vehicle is soiled, stained, or damaged due to negligence or inappropriate conduct.
            </li>
          </ul>
        </section>

        {/* Section 5: Cancellations & Amendments */}
        <section className="space-y-3">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">
            5. Cancellation & Amendments
          </h2>
          <p className="text-gray-600">
            All cancellations and itinerary amendments are governed by our dedicated{' '}
            <Link to="/cancellation-policy" className="text-[#FF7027] font-semibold hover:underline">
              Cancellation Policy
            </Link>. Customers enjoy free cancellation up to 24 hours before the scheduled pickup time.
          </p>
        </section>

        {/* Section 6: Liability & Force Majeure */}
        <section className="space-y-3">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">
            6. Limitation of Liability & Force Majeure
          </h2>
          <p className="text-gray-600">
            Salzburg Cab is not liable for delays, missed connections, or transport failures caused by events beyond our reasonable control (&ldquo;Force Majeure&rdquo;), including extreme Alpine blizzards, avalanche warnings, police road closures, severe highway traffic gridlock, or border customs delays. We will make every reasonable effort to provide alternative routing or backup vehicles.
          </p>
        </section>

        {/* Section 7: Applicable Law */}
        <section className="space-y-3">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">
            7. Applicable Law & Jurisdiction
          </h2>
          <p className="text-gray-600">
            These Terms are governed by and construed in accordance with the laws of the Republic of Austria, excluding the UN Convention on Contracts for the International Sale of Goods (CISG). The competent court in Salzburg, Austria, shall have exclusive jurisdiction over any disputes arising out of or in connection with our services.
          </p>
        </section>
      </div>
    </LegalLayout>
  )
}
